import { IbePriceModelCode } from '@gecko-akh/pkg-cruise-api-types/src/iris'
import dayjs from 'dayjs'
import { orderBy } from 'lodash-es'
import { Direction, Flight, FlightSegment } from '../../../bewotec/openapi'
import { handleError } from '../../../general/errorHandling'
import { DEFAULT_TIME_FORMAT, EXTENDED_DATE_FORMAT, convertFormat, getValidDateFormat } from '../../../general/helper'
import { FLIGHT_CLASS_SORT_ORDER } from '../../../general/master-data'
import { IDetailRoute } from '../cruise/interfaces'
import { mapPriceModelLabel } from '../helper'
import { DisplayFunctionFlightPrice } from '../price/interfaces'
import {
  IAirportData,
  IExpinetFlight,
  IExpinetFlightType,
  IExpinetFlightTypeMap,
  IExpinetSelectedFlight,
  IFlight,
  IFlightClass,
  IFlightPriceEntry,
  ITwoWayFlightSegments
} from './interfaces'

export const initFlights = (bewotecFlights: Flight[]) => {
  const aidaFlights: IExpinetFlight = { [Direction.RT]: [], [Direction.OJ]: [], [Direction.R]: [], [Direction.O]: [] }

  let flightId = 0

  for (const flight of bewotecFlights) {
    switch (flight.direction) {
      case Direction.O:
      case Direction.R:
        flightHelper.makeOneWayFlight(flightId++, flight, aidaFlights)
        break
      case Direction.RT:
      case Direction.OJ:
        flightHelper.makeTwoWayFlight(flightId++, flight, aidaFlights)
        break
    }
  }

  return aidaFlights
}

const makeOneWayFlight = (id: number, flight: Flight, aidaFlights: IExpinetFlight) => {
  const first = flight.flightSegments?.at(0)
  const last = flight.flightSegments?.at(-1)

  if (first === undefined || last === undefined) {
    AddError('makeOneWayFlight')
    return
  }

  const departure = mapAirportDataForFlight(
    !!first.publish,
    first.depAirportCode,
    first.depAirportName,
    first.depDate,
    first.depTime
  )
  const arrival = mapAirportDataForFlight(
    !!last.publish,
    last.arrAirportCode,
    last.arrAirportName,
    last.arrDate,
    last.arrTime
  )
  const mainFlight = mapFlightData(flight.direction === Direction.O ? last : first, flight.direction)

  let stopArrival
  let stopDeparture = null
  let feederFlight = null

  if (!!flight.flightSegments && flight.flightSegments.length > 1) {
    stopArrival = mapAirportDataForFlight(
      !!first.publish,
      first.arrAirportCode,
      first.arrAirportName,
      first.arrDate,
      first.arrTime
    )
    stopDeparture = mapAirportDataForFlight(
      !!last.publish,
      last.depAirportCode,
      last.depAirportName,
      last.depDate,
      last.depTime
    )
    feederFlight = mapFlightData(flight.direction === Direction.O ? first : last, flight.direction)
  }

  const f: IExpinetFlightType = {
    airlines: [],
    id,
    departure,
    stopArrival,
    stopDeparture,
    arrival,
    // TODO: remove fallbacks
    price: !!flight.pricings
      ? flight.pricings.reduce((accumulator, pricing) => accumulator + (pricing.amount ?? 0), 0)
      : 0,
    direction: flight.direction,
    mainFlight,
    feederFlight,
  }

  switch (flight.direction) {
    case Direction.O:
      const outFlight: IExpinetFlightTypeMap = {
        outboundFlight: f,
        inboundFlight: null,
        direction: Direction.O,
        price: f.price,
      }
      aidaFlights[Direction.O].push(outFlight)
      break
    case Direction.R:
      const inFlight: IExpinetFlightTypeMap = {
        outboundFlight: null,
        inboundFlight: f,
        direction: Direction.R,
        price: f.price,
      }
      aidaFlights[Direction.R].push(inFlight)
      break
  }
}

const makeTwoWayFlight = (id: number, flight: Flight, aidaFlights: IExpinetFlight) => {
  const firstConnection = flight.flightSegments?.at(0)
  const lastConnection = flight.flightSegments?.at(-1)

  if (!firstConnection || !lastConnection) {
    AddError('makeTwoWayFlight')
    return
  }

  const first: ITwoWayFlightSegments = {
    out: firstConnection,
    in: null,
  }
  const last: ITwoWayFlightSegments = {
    out: null,
    in: lastConnection,
  }

  let firstInIndex = 0

  for (let i = 0; i < flight.flightSegments!.length; ++i) {
    if (flight.flightSegments![i].direction === Direction.R) {
      first.in = flight.flightSegments![i]
      last.out = flight.flightSegments![i - 1]
      firstInIndex = i // index of first segment of inbound flight
      break
    }
  }

  {
    if (!first.out || !last.out) {
      AddError('makeTwoWayFlight')
      return
    }

    const departureOut = mapAirportDataForFlight(
      !!first.out.publish,
      first.out.depAirportCode,
      first.out.depAirportName,
      first.out.depDate,
      first.out.depTime
    )
    const arrivalOut = mapAirportDataForFlight(
      !!last.out.publish,
      last.out.arrAirportCode,
      last.out.arrAirportName,
      last.out.arrDate,
      last.out.arrTime
    )
    const mainFlightOut = mapFlightData(last.out, Direction.O)

    let stopArrivalOut
    let stopDepartureOut: IAirportData | null = null
    let feederFlightOut: IFlight | null = null

    if (firstInIndex > 1) {
      stopDepartureOut = mapAirportDataForFlight(
        !!last.out.publish,
        last.out.depAirportCode,
        last.out.depAirportName,
        last.out.depDate,
        last.out.depTime
      )
      stopArrivalOut = mapAirportDataForFlight(
        !!first.out.publish,
        first.out.arrAirportCode,
        first.out.arrAirportName,
        first.out.arrDate,
        first.out.arrTime
      )
      feederFlightOut = mapFlightData(first.out, Direction.O)
    }

    const outFlight: IExpinetFlightType = {
      airlines: [],
      id,
      departure: departureOut,
      arrival: arrivalOut,
      stopArrival: stopArrivalOut,
      stopDeparture: stopDepartureOut,
      price: !!flight.pricings
        ? flight.pricings.reduce((accumulator, pricing) => accumulator + (pricing.amount ?? 0), 0)
        : 0,
      direction: flight.direction,
      refIndex: -1,
      mainFlight: mainFlightOut,
      feederFlight: feederFlightOut,
    }

    if (!first.in || !last.in) {
      AddError('makeTwoWayFlight')
      return
    }

    const departure = mapAirportDataForFlight(
      !!first.in.publish,
      first.in.depAirportCode,
      first.in.depAirportName,
      first.in.depDate,
      first.in.depTime
    )
    const arrival = mapAirportDataForFlight(
      !!last.in.publish,
      last.in.arrAirportCode,
      last.in.arrAirportName,
      last.in.arrDate,
      last.in.arrTime
    )
    const mainFlight = mapFlightData(first.in, Direction.R)

    let stopArrival
    let stopDeparture: IAirportData | null = null
    let feederFlight: IFlight | null = null

    if (!!flight.flightSegments && flight.flightSegments.length - firstInIndex > 1) {
      stopDeparture = mapAirportDataForFlight(
        !!last.in.publish,
        last.in.depAirportCode,
        last.in.depAirportName,
        last.in.depDate,
        last.in.depTime
      )
      stopArrival = mapAirportDataForFlight(
        !!first.in.publish,
        first.in.arrAirportCode,
        first.in.arrAirportName,
        first.in.arrDate,
        first.in.arrTime
      )
      feederFlight = mapFlightData(last.in, Direction.R)
    }

    const inFlight: IExpinetFlightType = {
      airlines: [],
      arrival,
      departure,
      direction: flight.direction,
      feederFlight,
      id,
      mainFlight,
      price: flight.pricings
        ? flight.pricings.reduce((accumulator, pricing) => accumulator + (pricing.amount ?? 0), 0)
        : 0,
      refIndex: -1,
      stopArrival,
      stopDeparture,
    }

    inFlight.refIndex = aidaFlights[inFlight.direction as keyof IExpinetFlight].length - 1
    outFlight.refIndex = aidaFlights[outFlight.direction as keyof IExpinetFlight].length

    const twoWayFlight: IExpinetFlightTypeMap = {
      outboundFlight: outFlight,
      inboundFlight: inFlight,
      price: outFlight.price,
      direction: outFlight.direction,
    }

    if (outFlight.direction === Direction.RT) {
      aidaFlights[Direction.RT].push(twoWayFlight)
    }

    if (outFlight.direction === Direction.OJ) {
      aidaFlights[Direction.OJ].push(twoWayFlight)
    }
  }
}

export const mapAirportDataForFlight = (
  published: boolean,
  airportCode: string | undefined,
  airportName: string | undefined,
  date: string | undefined,
  time: string | undefined
): IAirportData => {
  return {
    airportId: airportCode ?? '',
    airportName: airportName ?? '',
    time: !!published && !!date && !!time ? mapFlightTime(date, time) : '',
  }
}

const mapFlightTime = (date: string, time: string) => {
  const dateTime = `${date} ${time}`
  return dayjs(dateTime).format(EXTENDED_DATE_FORMAT)
}

export const mapFlightData = (flight: FlightSegment, direction: Direction): IFlight => {
  let airline = {}

  if (!!flight.carrierNumber && flight.direction === direction) {
    airline = {
      name: flight.carrierName,
      id: flight.carrierCode,
      operatedBy: flight.operatedBy,
      operatedByName: flight.operatedByName,
      flightNumber: flight.carrierNumber,
    }
  }

  const flightClass = {
    id: flight.flightClassCode ?? '',
    name: flight.flightClassName ?? '',
  } as IFlightClass

  return {
    airline,
    flightClass,
  }
}

export const sortFlightsByAirportName = (flightsToSort: IExpinetFlight) => {
  flightsToSort[Direction.O] = sortFlights(flightsToSort[Direction.O])
  flightsToSort[Direction.RT] = sortFlights(flightsToSort[Direction.RT])
  flightsToSort[Direction.OJ] = sortFlights(flightsToSort[Direction.OJ])

  flightsToSort[Direction.R].sort((a, b) => {
    const sortedAirportsByName = (a.inboundFlight?.arrival.airportName || '').localeCompare(
      b.inboundFlight?.arrival.airportName || ''
    )
    const sortedAirportsByClass =
      FLIGHT_CLASS_SORT_ORDER.indexOf(a.inboundFlight?.mainFlight.flightClass.id || '') >
      FLIGHT_CLASS_SORT_ORDER.indexOf(b.inboundFlight?.mainFlight.flightClass.id || '')
        ? 1
        : -1

    return sortedAirportsByName || sortedAirportsByClass
  })
}

const sortFlights = (flightsToSort: IExpinetFlightTypeMap[]) => {
  return flightsToSort.sort((a, b) => {
    const sortedAirportsByName = (a.outboundFlight?.departure.airportName || '').localeCompare(
      b.outboundFlight?.departure.airportName || ''
    )
    const sortedAirportsByClass =
      FLIGHT_CLASS_SORT_ORDER.indexOf(a.outboundFlight?.mainFlight.flightClass.id || '') >
      FLIGHT_CLASS_SORT_ORDER.indexOf(b.outboundFlight?.mainFlight.flightClass.id || '')
        ? 1
        : -1

    return sortedAirportsByName || sortedAirportsByClass
  })
}

export const chooseFlight = (
  route: IDetailRoute | null,
  aidaFlights: IExpinetFlight
): IExpinetSelectedFlight | null => {
  const needOutboundOnly = !route?.germanDeparture && !!route?.germanArrival
  const needInboundOnly = !!route?.germanDeparture && !route?.germanArrival

  const outboundOneWays = aidaFlights[Direction.O]
  const inboundOneWays = aidaFlights[Direction.R]
  const aidaRoundTrips = aidaFlights[Direction.RT]
  const aidaOpenJaws = aidaFlights[Direction.OJ]

  if (needOutboundOnly && !!outboundOneWays.length) {
    const cheapestFlight = findCheapestFlight(outboundOneWays)
    return createFlight(cheapestFlight, Direction.O)
  } else if (needInboundOnly && !!inboundOneWays.length) {
    const cheapestFlight = findCheapestFlight(inboundOneWays)
    return createFlight(cheapestFlight, Direction.R)
  } else if (!!aidaRoundTrips.length) {
    const cheapestFlight = findCheapestFlight(aidaRoundTrips)
    return createFlight(cheapestFlight, Direction.RT)
  } else if (!!aidaOpenJaws.length) {
    const cheapestFlight = findCheapestFlight(aidaOpenJaws)
    return createFlight(cheapestFlight, Direction.OJ)
  }

  return null
}

const createFlight = (flight: IExpinetFlightTypeMap, direction: Direction): IExpinetSelectedFlight | null => {
  const useFlight: IExpinetFlightType | null = direction === Direction.R ? flight.inboundFlight : flight.outboundFlight

  return useFlight
    ? {
        id: useFlight.id,
        price: flight.price,
        direction,
        outboundFlight: flight.outboundFlight,
        inboundFlight: flight.inboundFlight,
      }
    : null
}

const findCheapestFlight = (flights: IExpinetFlightTypeMap[]): IExpinetFlightTypeMap => {
  const orderedFlights = orderBy(flights, ['price'], ['asc'])
  return orderedFlights[0]
}

export const message = (
  flights: IExpinetFlight | null,
  priceModel: IbePriceModelCode,
  direction: 'inbound' | 'outbound'
) => {
  if (!flights) return 'Lade Flüge ...'
  
  if (countFlights(flights, direction) === 0) return `Kein Flug verfügbar im Tarif ${mapPriceModelLabel(priceModel)}.`
  
  if (direction === 'outbound') return 'Kein Hinflug ausgewählt'
  else if (direction === 'inbound') return 'Kein Rückflug ausgewählt'

  return ''
}

const countFlights = (flights: IExpinetFlight | undefined, direction: 'inbound' | 'outbound') => {
  if (!flights) return 0

  const twoWayFlights = flights[Direction.RT].length + flights[Direction.OJ].length

  if (direction === 'outbound') return twoWayFlights + flights[Direction.O].length
  else if (direction === 'inbound') return twoWayFlights + flights[Direction.R].length

  return 0
}

export const makeFlightEntry = (
  flight: IExpinetFlightTypeMap,
  displayFlightPrice: DisplayFunctionFlightPrice
): IFlightPriceEntry => {
  let display: string = ''
  const flightItem: IExpinetFlightType | null = flight.outboundFlight ?? flight.inboundFlight

  if (flight.direction === Direction.RT || flight.direction === Direction.OJ) {
    const departure = flight?.outboundFlight?.departure
    const arrival = flight?.inboundFlight?.arrival

    const departureTime = !!departure?.time
      ? ` ${convertFormat(departure.time, getValidDateFormat(departure.time), DEFAULT_TIME_FORMAT)}`
      : ''

    const arrivalTime = !!arrival?.time
      ? ` ${convertFormat(arrival.time, getValidDateFormat(arrival.time), DEFAULT_TIME_FORMAT)}`
      : ''

    display = `${departure?.airportName || ''}${departureTime} \u2013 AIDA \u2013 ${arrivalTime} ${
      arrival?.airportName ?? ''
    } (${flight.inboundFlight!.mainFlight.flightClass.name})`
  } else if (flightItem) {
    const departureTime = flightItem.departure?.time
      ? ` ${convertFormat(
          flightItem.departure.time,
          getValidDateFormat(flightItem.departure.time),
          DEFAULT_TIME_FORMAT
        )}`
      : ''

    const arrivalTime = flightItem.arrival?.time
      ? ` ${convertFormat(flightItem.arrival.time, getValidDateFormat(flightItem.arrival.time), DEFAULT_TIME_FORMAT)}`
      : ''

    switch (flightItem.direction) {
      case Direction.O:
        display = `${flightItem.departure.airportName}${departureTime} (${flightItem.mainFlight.flightClass.name})`
        break
      case Direction.R:
        display = `${flightItem.arrival.airportName}${arrivalTime} (${flightItem.mainFlight.flightClass.name})`
        break
    }
  }

  return {
    inputDisplay: display,
    listDisplay: display,
    price: displayFlightPrice(flight),
    flight,
  }
}

const flightsAreEqual = (f: IExpinetFlightType, g: IExpinetSelectedFlight) => {
  if (!f || !g) {
    return false
  }

  return f.id === g.id
}

const AddError = (origin?: any) => {
  const originName = `flight - ${origin}`
  handleError({ error: new Error('Flight connection is null'), origin: originName })
}

export const flightHelper = {
  makeOneWayFlight,
  makeTwoWayFlight,
  message,
  flightsAreEqual,
  chooseFlight,
  sortFlightsByAirportName,
  initFlights,
}

// --------------- Export for testing Purposes Only ---------------

export const exportForTesting = {
  AddError,
  countFlights,
  findCheapestFlight,
  createFlight,
  sortFlights,
  mapFlightTime,
}
